import React, { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { formatNotificationTime } from '../utils/notifications';
import { withoutEmDashes } from '../utils/displayText';

// Inline SVGs (the app's convention — no icon library). 24x24, currentColor.
const ICONS = {
    alert: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v4" /><path d="M12 16h.01" /></svg>
    ),
    document: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M9 13h6" /><path d="M9 17h6" /></svg>
    ),
    wrench: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a4 4 0 0 0-5.2 5.2L4 17v3h3l5.5-5.5a4 4 0 0 0 5.2-5.2l-2.6 2.6-2.1-.5-.5-2.1z" /></svg>
    ),
    calendar: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" /><path d="m9 15 2 2 4-4" /></svg>
    ),
    folder: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5l2 3h7a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2z" /></svg>
    ),
    gift: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="8" width="18" height="4" rx="1" /><path d="M12 8v13" /><path d="M5 12v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7" /><path d="M12 8C12 8 11 3 8 3a2.5 2.5 0 0 0 0 5z" /><path d="M12 8c0 0 1-5 4-5a2.5 2.5 0 0 1 0 5z" /></svg>
    ),
    dollar: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v10" /><path d="M14.5 9.5a2.5 2 0 0 0-2.5-1.5c-1.4 0-2.5.8-2.5 2s1.1 1.7 2.5 2 2.5.8 2.5 2-1.1 2-2.5 2a2.5 2 0 0 1-2.5-1.5" /></svg>
    ),
    message: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
    ),
};

// tone -> icon bubble bg, icon color, and the unread accent bar color.
const TONES = {
    red: { bubble: 'bg-[#fde8e8]', icon: 'text-[#dc2626]', accent: '#dc2626' },
    blue: { bubble: 'bg-[#F2F2F2]', icon: 'text-[#414141]', accent: '#D81F26' },
    orange: { bubble: 'bg-[#fef1d6]', icon: 'text-[#d97706]', accent: '#d97706' },
    green: { bubble: 'bg-[#e8fbef]', icon: 'text-[#15803d]', accent: '#15803d' },
    slate: { bubble: 'bg-[#f2f2f2]', icon: 'text-[#414141]', accent: '#a2a2a2' },
    amber: { bubble: 'bg-[#fef3c7]', icon: 'text-[#b45309]', accent: '#b45309' },
    teal: { bubble: 'bg-[#d9f7ef]', icon: 'text-[#0d9488]', accent: '#0d9488' },
};

// One notification. The title button opens it, and a stretched ::after makes the whole
// card the click/tap target. Delete and "Show more" are separate real buttons layered
// above it (a button nested inside a button is invalid and unreliable for keyboards and
// screen readers).
function NotificationCard({ notification, onOpen, onDismiss, entranceDelay }) {
    const tone = TONES[notification.tone] || TONES.slate;
    const isUnread = !notification.isRead;
    const title = withoutEmDashes(notification.title);
    const message = withoutEmDashes(notification.message);
    const messageRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [clamped, setClamped] = useState(false);
    const messageId = `notification-message-${notification.id}`;

    // Show "Show more" only when the two-line clamp actually hides text.
    useLayoutEffect(() => {
        const el = messageRef.current;
        if (!el || expanded) return undefined;
        const measure = () => setClamped(el.scrollHeight > el.clientHeight + 1);
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [message, expanded]);

    return (
        <div
            className={`group relative w-full overflow-hidden rounded-2xl border text-left shadow-sm transition-[background-color,border-color,opacity] duration-500 focus-within:ring-2 focus-within:ring-[#D81F26]/25 ${
                isUnread
                    ? 'border-[#d9d9d9] bg-[#f8f8f8] hover:bg-[#f5f5f5]'
                    : 'border-[#d9d9d9] bg-white hover:bg-[#F9F9F9]'
            } ${entranceDelay != null ? 'animate-dash-slide-in-right' : ''}`}
            style={entranceDelay != null ? { animationDelay: `${entranceDelay}ms` } : undefined}
        >
            {/* Always rendered so marking read fades the accent bar out instead of snapping it away. */}
            <span
                className={`absolute inset-y-0 left-0 w-1.5 transition-opacity duration-500 ${isUnread ? 'opacity-100' : 'opacity-0'}`}
                style={{ backgroundColor: tone.accent }}
                aria-hidden="true"
            />
            <div className="flex items-start gap-3.5 px-4 py-3.5 pl-5 md:px-5 md:pl-6">
                <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${tone.bubble} ${tone.icon}`} aria-hidden="true">
                    {ICONS[notification.icon] || ICONS.alert}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <h3 className="text-[15px] font-black tracking-tight text-[#414141]">
                            <button
                                type="button"
                                onClick={() => onOpen(notification)}
                                aria-describedby={messageId}
                                className="text-left after:absolute after:inset-0 after:content-[''] focus-visible:outline-none"
                            >
                                {title}
                            </button>
                        </h3>
                        {notification.demo && (
                            <span className="rounded-full bg-[#fef3c7] px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-[#b45309]">Demo</span>
                        )}
                        {isUnread && (
                            <span className="rounded-full bg-[#414141] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-white">Unread</span>
                        )}
                    </div>
                    <p
                        ref={messageRef}
                        id={messageId}
                        className={`mt-1 break-words text-[14px] font-medium leading-relaxed text-[#565656] ${expanded ? '' : 'line-clamp-2'}`}
                    >
                        {message}
                    </p>
                    {(clamped || expanded) && (
                        <button
                            type="button"
                            onClick={() => setExpanded((v) => !v)}
                            aria-expanded={expanded}
                            aria-controls={messageId}
                            className="relative z-10 mt-1 text-[12px] font-black text-[#D81F26] hover:underline"
                        >
                            {expanded ? 'Show less' : 'Show more'}
                        </button>
                    )}
                    <p className="mt-2 text-[12px] font-semibold text-[#939393]">
                        {notification.category} · <span className="whitespace-nowrap">{formatNotificationTime(notification.timestamp)}</span>
                    </p>
                </div>
                <div className="relative z-10 -mr-1 flex shrink-0 items-center gap-0.5">
                    {/* Always visible on touch screens; with a mouse it appears on hover or keyboard focus. */}
                    <button
                        type="button"
                        onClick={() => onDismiss(notification.id)}
                        aria-label={`Delete notification: ${title}`}
                        title="Delete notification"
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-[#939393] transition hover:bg-black/5 hover:text-[#c0392b] focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D81F26]/40 group-focus-within:opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18" /><path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" /><path d="M19 6l-1 14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1L5 6" /></svg>
                    </button>
                    <svg className="pointer-events-none text-[#c3c3c3] transition-colors group-hover:text-[#414141]" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m9 18 6-6-6-6" />
                    </svg>
                </div>
            </div>
        </div>
    );
}

export default function NotificationsPage() {
    const { notifications = [], markNotificationRead, markAllNotificationsRead, dismissNotification } = useOutletContext() || {};
    const navigate = useNavigate();
    const unreadCount = notifications.reduce((n, item) => (item.isRead ? n : n + 1), 0);

    // Entrance slide for unread items, staggered 40ms apart (capped at 400ms). Each id is
    // decided once, the first time it's seen: unread ones get a delay, read ones just
    // appear. Recorded in a ref so re-renders (e.g. marking read) never replay it.
    const entranceRef = useRef({ seen: new Set(), delays: new Map() });
    let staggerIndex = 0;
    notifications.forEach((item) => {
        const { seen, delays } = entranceRef.current;
        if (seen.has(item.id)) return;
        seen.add(item.id);
        if (!item.isRead) {
            delays.set(item.id, Math.min(staggerIndex * 40, 400));
            staggerIndex += 1;
        }
    });

    // Tapping a notification marks it read and takes the user to the screen where
    // they can act on it (its `link`, e.g. /garage/chat/<id> or /glovebox).
    const openNotification = (notification) => {
        if (!notification.isRead && markNotificationRead) markNotificationRead(notification.id);
        const link = notification.link;
        const docId = notification?.metadata?.doc_id;
        // A Glove Box document notification should open that exact document (the
        // diagnostic report / file), not just land on the Glove Box list.
        if (docId && (!link || link === '/glovebox')) {
            navigate('/glovebox', { state: { tab: 'documents', openDocId: docId } });
            return;
        }
        if (link && typeof link === 'string' && link.startsWith('/')) {
            navigate(link);
        }
    };

    return (
        <div className="h-full w-full overflow-y-auto bg-[#F9F9F9] custom-scrollbar relative">
            <div className="mx-auto w-full max-w-3xl px-4 pt-8 pb-12 md:px-6 md:pt-10">
                <header className="mb-7 flex flex-wrap items-start justify-between gap-4 animate-in fade-in slide-in-from-top-4 duration-700">
                    <div>
                        <h1 className="text-2xl md:text-[28px] font-black tracking-tight text-black">Notifications</h1>
                        <p className="mt-1.5 text-[15px] font-medium text-[#414141]">
                            {unreadCount > 0 ? `${unreadCount} unread` : 'No unread notifications'}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => markAllNotificationsRead && markAllNotificationsRead()}
                        disabled={unreadCount === 0}
                        className={`flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-bold transition-colors ${
                            unreadCount === 0
                                ? 'cursor-default border-[#d9d9d9] bg-white text-[#c3c3c3]'
                                : 'border-[#d9d9d9] bg-white text-[#414141] shadow-sm hover:bg-[#f5f5f5]'
                        }`}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m2 12 5 5L18 6" /><path d="m13 17 1 1 8-9" /></svg>
                        Mark all as read
                    </button>
                </header>

                {notifications.length === 0 ? (
                    <div className="rounded-2xl border border-[#d9d9d9] bg-white px-6 py-16 text-center shadow-sm">
                        <p className="text-[15px] font-semibold text-[#414141]">No notifications yet.</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-2.5">
                        {notifications.map((notification) => (
                            <NotificationCard
                                key={notification.id}
                                notification={notification}
                                onOpen={openNotification}
                                onDismiss={dismissNotification}
                                entranceDelay={entranceRef.current.delays.get(notification.id)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
